import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

// Per-table hover state for the row/column drag handles. Lives inside the
// <TableProvider> boundary so each table has its own hover state — moving
// the cursor between two tables in the same doc doesn't bleed handle
// visibility between them.
//
// "Sticky" hide: cells call hideDeferred() on mouseleave, which schedules a
// short timeout. If the pointer moves onto the floating handle, the handle's
// own onMouseEnter calls cancelHide() to keep itself visible. This is the
// Notion pattern — without it the handle vanishes the moment the pointer
// crosses from cell border to handle.
//
// Callbacks and hovered state are SPLIT into two contexts so cells that only
// call `show()` on mouseenter do not re-render when hover moves. The overlay
// is the only consumer of `hovered`. On a 4k-cell CAM, a combined context
// re-rendered every cell on every mouseenter.

export type TableHoverState = { row: number | null; col: number | null };

interface HoverCallbacks {
    show: (row: number, col: number) => void;
    hideDeferred: () => void;
    cancelHide: () => void;
    clearImmediately: () => void;
}

type ContextValue = HoverCallbacks & { hovered: TableHoverState };

const TableHoverCallbacksContext = createContext<HoverCallbacks | null>(null);
const TableHoverStateContext = createContext<TableHoverState>({ row: null, col: null });

const HIDE_DELAY_MS = 120;

const NOOP_CALLBACKS: HoverCallbacks = {
    show: () => {},
    hideDeferred: () => {},
    cancelHide: () => {},
    clearImmediately: () => {},
};

export function TableHoverProvider({ children }: { children: React.ReactNode }) {
    const [hovered, setHovered] = useState<TableHoverState>({ row: null, col: null });
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelHide = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const show = useCallback(
        (row: number, col: number) => {
            cancelHide();
            setHovered((prev) => (prev.row === row && prev.col === col ? prev : { row, col }));
        },
        [cancelHide],
    );

    const hideDeferred = useCallback(() => {
        cancelHide();
        timerRef.current = setTimeout(() => {
            setHovered({ row: null, col: null });
            timerRef.current = null;
        }, HIDE_DELAY_MS);
    }, [cancelHide]);

    const clearImmediately = useCallback(() => {
        cancelHide();
        setHovered({ row: null, col: null });
    }, [cancelHide]);

    const callbacks = useMemo<HoverCallbacks>(
        () => ({ show, hideDeferred, cancelHide, clearImmediately }),
        [show, hideDeferred, cancelHide, clearImmediately],
    );

    return (
        <TableHoverCallbacksContext.Provider value={callbacks}>
            <TableHoverStateContext.Provider value={hovered}>{children}</TableHoverStateContext.Provider>
        </TableHoverCallbacksContext.Provider>
    );
}

export function useTableHoverCallbacks(): HoverCallbacks {
    return useContext(TableHoverCallbacksContext) ?? NOOP_CALLBACKS;
}

export function useTableHover(): ContextValue {
    const callbacks = useContext(TableHoverCallbacksContext);
    const hovered = useContext(TableHoverStateContext);
    if (!callbacks) {
        // Defensive: cells may render in non-table contexts (paste preview,
        // history view); return a no-op shape so they don't crash.
        return { hovered: { row: null, col: null }, ...NOOP_CALLBACKS };
    }
    return { hovered, ...callbacks };
}
